import { showBendingCheckSummary } from './bending-summary-view.js'
import { showShearCheckSummary } from './shear-summary-view.js'
import { showSmallLengthWithUnits } from 'ws-units'

export function showDesignSummary(project, results) {
  document.getElementById('design-summary-div').classList.remove('d-none')
  const bendingUnity = results["bending_checks"]["controlling_unity"];
  const shearUnity = results["shear_checks"]["controlling_unity"];
  const deflectionUnity = results["deflection_checks"]["controlling_unity"];
  const allOk = bendingUnity < 1.0 && shearUnity < 1.0 && deflectionUnity < 1.0;

  if(allOk) {
    // dispatch design-succeeded 
    let message = `${project.designTypeStr} - ${project.shapeName}`
    let materialName = project.isCustom ? "" : project.materialName
    if (project.isEngineeredLumber) {
      materialName = project.materialGradeName;
    } else if (project.isWood) {
      materialName += ` ${project.materialGradeName}`;
    }
    message += ` ${materialName}`
    document.dispatchEvent(new CustomEvent('design-success', {detail: message}))
  } else {
    // dispatch design-failed
    let title = "Design Failed"
    let message = "Adjust shape size, material or span length."
    if(bendingUnity > 1.0) {
      title = "Bending Capacity Exceeded" 
      //title = `Bending Capacity Exceeded - ${results["bending_checks"]["controlling_code_equation"]}`
      message = "Try a larger beam."
      const controllingCodeMessage = results["bending_checks"]["controlling_code_equation"]
      if (controllingCodeMessage && controllingCodeMessage.includes('Slenderness Ratio')) {
        message = controllingCodeMessage
      }
    } else if(shearUnity > 1.0) {
      title = "Shear Capacity Exceeded"
      //title = `Shear Capacity Exceeded - ${results["shear_checks"]["controlling_code_equation"]}`
      message = "Try a larger beam. "
    } else if(deflectionUnity > 1.0) {
      title = `Deflection Limits Exceeded`
      message = 'Try a larger beam.' //`${results["deflection_checks"]["controlling_case"]}`
      
    } 
    document.dispatchEvent(new CustomEvent('design-failed', {detail: {title: title, message: message}}))
  }

  showBendingCheckSummary(project, results)
  showShearCheckSummary(project, results)
  showDeflectionCheckSummary(project, results)
  showDesignInfoSummary(project, results)
}

export function configureCardAppearance(elementId, designOk) {
  const element = document.getElementById(elementId)
  if (designOk) {
    element.classList.remove('ws-design-failed')
    element.classList.add('ws-design-succeeded')
  } else {
    element.classList.add('ws-design-failed')
    element.classList.remove('ws-design-succeeded')
  }
}

function makeRow(label, value) {
  return `<tr><td>${label}</td><td><span style='float:right;'>${value}</span></td></tr>`;
}

function showDeflectionCheckSummary(project, results) {
  const design = project.design;
  const checks = results["deflection_checks"];
  const controlling_unity = checks["controlling_unity"];
  const controlling_case = checks["controlling_case"];
  const max_dy = checks["max_dy"];
  const success = controlling_unity < 1.0;
  const designStatusStr = success ? '<i class="fa fa-check"></i>' : '<i class="fa fa-remove"></i>';
  configureCardAppearance('deflection-design-panel', success);

  let rows = [
    makeRow("Based On", "Service Cases"),
    makeRow("Controlling Service Case", controlling_case),
    makeRow("Max Dy", showSmallLengthWithUnits(max_dy)),
    makeRow("Live Load Limit", `L/${design.liveLoadLimit}`),
    makeRow("Dead + Live Limit", `L/${design.deadPlusLiveLoadLimit}`),
    makeRow("Snow or Wind Limit", `L/${design.snowOrWindLoadLimit}`),
    makeRow("Total Load Limit", `L/${design.totalLoadLimit}`)
  ];

  if (project.isEngineeredLumber) {
    rows.push(makeRow("Shear Deformation Included", "Asx = A(5 + 5&nu;)/(6 + 5&nu;)"));
  }

  const str = `
    <div class="card-header">
      <div class="fs-6">
        ${designStatusStr} Deflection
        <span class="float-end">${controlling_unity.toFixed(2)}</span>
      </div>
    </div>
    <div class="bg-white rounded-2">
      <table class="table table-borderless">
        ${rows.join('')}
      </table>
    </div>
  `;
  document.getElementById("deflection-design-panel").innerHTML = str;
}

function showDesignInfoSummary(project, results) {
  const design = project.design;
  const checks = results["deflection_checks"];
  const controlling_unity = checks["controlling_unity"];
  const success = controlling_unity < 1.0;
  configureCardAppearance('design-info-summary-panel', success);

  let rows = [
    makeRow("Shape Type", project.shapeName)
  ];

  if (project.isEngineeredLumber || project.isWood) {
    rows.push(makeRow("Number of Plies", project.numberOfPlys));
  }

  if (project.shape.flitch) {
    const { plateThickness: thickness, plateHeight: height, numberOfPlates: nPlates } = project.shape.flitch;
    rows.push(makeRow("Flitch Steel", `${nPlates}-${thickness}&times;${height}`));
  }

  rows.push(makeRow(`Weight${project.shape.flitch ? " (includes flitch steel)" : ""}`, project.shapeWeightString));
  rows.push(makeRow("Self Weight", design.includeSelfWeight ? "Included" : "Not Included"));

  if (!project.isEngineeredLumber && !project.isCustom && !project.isFormTechShape && project.crossSectionRotation > 0) {
    rows.push(makeRow("Iy", project.shapeIyString));
    rows.push(makeRow("Cross Section Rotation", `${project.beam.crossSectionRotation}&deg;`));
  } else {
    rows.push(makeRow(`Ix${project.shape.flitch ? " (includes flitch steel)" : ""}`, project.shapeIxString));
  }

  // Bracing
  let bracingValue = "Custom";
  if (project.isEngineeredLumber || design.bracingType === 'continuous') {
    bracingValue = "Continuous";
  } else if (design.bracingType === "equal") {
    bracingValue = `Braced @ ${showSmallLengthWithUnits(project.design.getBraceSpacing())}`;
  } else if (design.bracingType === "unbraced") {
    bracingValue = "None";
  }
  rows.push(makeRow("Bracing", bracingValue));

  if (!project.isEngineeredLumber && !project.isCustom && !project.isFormTechShape && design.bracingType === "custom") {
    rows.push(makeRow("At Inflection Points", design.bracesAtInflectionPoints ? "Yes" : "No"));
  }

  if (project.isWood && design.designType !== 'None' && (design.useLambdaOrCd || design.useRepetitiveMemberFactor)) {
    let factors = [];
    if (design.useLambdaOrCd && design.designType === 'LRFD') {
      factors.push('&lambda; (time effect)');
    } else if (design.useLambdaOrCd && design.designType === 'ASD') {
      factors.push('C<sub>D</sub> (load duration)');
    }
    if (design.useRepetitiveMemberFactor) {
      factors.push('Repetitive Member');
    }
    rows.push(makeRow(factors.join(', '), "Included"));
  }

  const str = `
    <div class="card-header">
      <div class="fs-6">
        Design Info
        <span class="float-end">${project.designTypeStr}</span>
      </div>
    </div>
    <div class='bg-white rounded-2'>
      <table class='table table-borderless'>
        ${rows.join('')}
      </table>
    </div>
  `;
  document.getElementById("design-info-summary-panel").innerHTML = str;
}
